import React, { useMemo } from "react";
import { Info, Layers, Send, Sparkles, Ticket } from "lucide-react";
import {
  Btn,
  Callout,
  Card,
  CardBody,
  CardHeader,
  KPI,
  KV,
} from "../../loyalty/components/primitives";

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

const OverviewTab = ({ coupons = [], loading, goTo }) => {
  const now = Date.now();

  const stats = useMemo(() => {
    const active = coupons.filter(
      (c) => c.status === "active" && (c.valid_to ?? Infinity) >= now
    );
    const codesGenerated = coupons.reduce(
      (acc, c) =>
        acc + Number(c.codes_total ?? c.codes_generated ?? c.codes_count ?? 0),
      0
    );
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const monthStartMs = startOfMonth.getTime();
    const redemptionsThisMonth = coupons.reduce((acc, c) => {
      const recent =
        c.used_this_month ??
        (c.last_used_at && new Date(c.last_used_at).getTime() >= monthStartMs
          ? c.used_count || 0
          : 0);
      return acc + Number(recent || 0);
    }, 0);
    return {
      activeCount: active.length,
      totalCount: coupons.length,
      codesGenerated,
      redemptionsThisMonth,
    };
  }, [coupons, now]);

  const recent = useMemo(
    () =>
      [...coupons]
        .sort(
          (a, b) =>
            new Date(b.updated_at || b.created_at || 0).getTime() -
            new Date(a.updated_at || a.created_at || 0).getTime()
        )
        .slice(0, 5),
    [coupons]
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">Coupons Overview</h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[680px]">
            Author single shareable codes (like <span className="font-mono">MONSOON20</span>) or
            generate series of unique codes (like <span className="font-mono">BIRTHDAY-A1B2C3</span>) for SMS / WhatsApp / email campaigns.
          </p>
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={() => goTo("list")}>
            <Ticket size={15} />
            All coupons
          </Btn>
          <Btn variant="primary" onClick={() => goTo("create")}>
            <Sparkles size={15} />
            New coupon
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          icon={<Ticket size={18} />}
          tone="blue"
          label="Active coupons"
          value={fmtNum(stats.activeCount)}
          delta={`${fmtNum(stats.totalCount)} total`}
        />
        <KPI
          icon={<Layers size={18} />}
          tone="purple"
          label="Codes generated"
          value={fmtNum(stats.codesGenerated)}
          delta="Across all series"
        />
        <KPI
          icon={<Send size={18} />}
          tone="green"
          label="Redemptions this month"
          value={fmtNum(stats.redemptionsThisMonth)}
          delta="Rolling 30d"
        />
        <KPI
          icon={<Sparkles size={18} />}
          tone="gold"
          label="Drafts"
          value={fmtNum(coupons.filter((c) => c.status === "draft").length)}
          delta="Awaiting activation"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-start">
        <Card>
          <CardHeader
            icon={<Ticket size={18} />}
            tone="blue"
            title="Recently updated"
            action={
              <Btn size="sm" variant="ghost" onClick={() => goTo("list")}>
                View all
              </Btn>
            }
          />
          <CardBody tight>
            {loading ? (
              <div className="text-[13px] text-[#64748B] py-3">Loading…</div>
            ) : recent.length === 0 ? (
              <div className="text-[13px] text-[#64748B] py-3">
                No coupons yet. Create your first one to get started.
              </div>
            ) : (
              recent.map((c) => (
                <KV
                  key={c.id}
                  label={
                    <span className="flex items-center gap-2">
                      <span className="font-semibold text-[#0F172A]">{c.name}</span>
                      <span className="text-[11px] text-[#94A3B8] font-mono">
                        {c.discount_type || ""}
                      </span>
                    </span>
                  }
                  value={
                    <span className="tabular-nums text-[12px] text-[#64748B]">
                      {c.status || "draft"}
                    </span>
                  }
                />
              ))
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            icon={<Sparkles size={18} />}
            tone="amber"
            title="How it works"
          />
          <CardBody>
            <div className="text-[12.5px] text-[#475569] leading-relaxed space-y-2">
              <div>
                <b className="text-[#0F172A]">Single</b> — one shared code that many customers can redeem.
                Use it for general-public promotions.
              </div>
              <div>
                <b className="text-[#0F172A]">Series</b> — generate thousands of unique codes for targeted
                campaigns. Each code is single-use by design.
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <Callout tone="blue" icon={<Info size={16} />}>
        Coupon edits apply to <b>future redemptions</b> only. Already-redeemed coupons keep the rate they were captured at.
      </Callout>
    </div>
  );
};

export default OverviewTab;
